/**
 * NRRWaterfallView — Visualises the NRR decomposition as a waterfall chart
 * with a "current" series and an "improved" overlay showing 2-3yr aspirational targets.
 */

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import type { NrrWaterfallItem } from "../types/diagnostic";

interface NRRWaterfallViewProps {
  waterfall: NrrWaterfallItem[];
  clientName: string;
}

/** Colour palette for waterfall bars. */
const BAR_COLORS: Record<string, { current: string; improved: string }> = {
  starting_arr: { current: "#2563eb", improved: "#93c5fd" },
  churn: { current: "#dc2626", improved: "#fca5a5" },
  downsell: { current: "#ea580c", improved: "#fdba74" },
  gross_retention: { current: "#6b7280", improved: "#d1d5db" },
  upsell: { current: "#16a34a", improved: "#86efac" },
  cross_sell: { current: "#0891b2", improved: "#67e8f9" },
  price_up: { current: "#7c3aed", improved: "#c4b5fd" },
  net_retention: { current: "#2563eb", improved: "#93c5fd" },
};

/** Determines if a waterfall item is a "total" bar (starting/gross/net). */
function isTotalBar(id: string): boolean {
  return ["starting_arr", "gross_retention", "net_retention"].includes(id);
}

/** Builds waterfall chart data with cumulative baselines. */
function buildWaterfallData(
  items: NrrWaterfallItem[],
  field: "current_pct" | "improved_pct"
) {
  let cumulative = 0;
  return items.map((item) => {
    const value = item[field];
    if (item.id === "starting_arr") {
      cumulative = value;
      return { ...item, base: 0, value, cumulative };
    }
    if (isTotalBar(item.id)) {
      // Total bars show from 0 to their value
      cumulative = value;
      return { ...item, base: 0, value, cumulative };
    }
    const base = cumulative;
    cumulative += value;
    return { ...item, base: Math.min(base, cumulative), value: Math.abs(value), cumulative };
  });
}

export function NRRWaterfallView({ waterfall, clientName }: NRRWaterfallViewProps) {
  const [showDetails, setShowDetails] = useState(false);

  const currentData = useMemo(() => buildWaterfallData(waterfall, "current_pct"), [waterfall]);
  const improvedData = useMemo(() => buildWaterfallData(waterfall, "improved_pct"), [waterfall]);

  /** Merge current + improved into a single dataset for the chart. */
  const chartData = useMemo(() => {
    return currentData.map((c, i) => {
      const imp = improvedData[i];
      return {
        label: c.label,
        id: c.id,
        // Current bar: invisible base + visible portion
        currentBase: c.base,
        currentValue: c.value,
        // Improved bar: invisible base + visible portion
        improvedBase: imp.base,
        improvedValue: imp.value,
        // Raw pct values for tooltip
        currentPct: waterfall[i].current_pct,
        improvedPct: waterfall[i].improved_pct,
        explanation: waterfall[i].explanation,
      };
    });
  }, [currentData, improvedData, waterfall]);

  /** Current and improved NRR totals. */
  const currentNrr = waterfall.find((w) => w.id === "net_retention")?.current_pct ?? 0;
  const improvedNrr = waterfall.find((w) => w.id === "net_retention")?.improved_pct ?? 0;
  const nrrDelta = improvedNrr - currentNrr;

  return (
    <div className="mck-card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            NRR Waterfall — Current vs. Improved
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Decomposition of {clientName}&apos;s NRR into retention and expansion components, with 2-3 year improvement targets
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-text-tertiary">Current NRR</p>
            <p className="text-lg font-bold text-text-primary">{currentNrr}%</p>
          </div>
          <TrendingUp className="w-5 h-5 text-green-600" />
          <div className="text-right">
            <p className="text-xs text-text-tertiary">Target NRR</p>
            <p className="text-lg font-bold text-green-600">
              {improvedNrr}%{" "}
              <span className="text-sm font-normal">(+{nrrDelta}pp)</span>
            </p>
          </div>
        </div>
      </div>

      {/* Waterfall Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barGap={-20} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#4a4a4a" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e5e5" }}
              interval={0}
              angle={-20}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={(v: number) => `${v}%`}
              domain={[0, 120]}
              axisLine={false}
              tickLine={false}
            />
            <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="4 3" />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                if (!d) return null;
                return (
                  <div className="bg-white border border-surface-borderLight rounded-lg shadow-mck p-3 max-w-xs">
                    <p className="font-semibold text-sm text-text-primary">{d.label}</p>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-text-secondary">
                        Current:{" "}
                        <strong>
                          {d.currentPct > 0 ? "+" : ""}
                          {d.currentPct}%
                        </strong>
                      </span>
                      <span className="text-green-700">
                        Target:{" "}
                        <strong>
                          {d.improvedPct > 0 ? "+" : ""}
                          {d.improvedPct}%
                        </strong>
                      </span>
                    </div>
                    {d.explanation && (
                      <p className="text-xs text-text-tertiary mt-2 leading-relaxed">{d.explanation}</p>
                    )}
                  </div>
                );
              }}
            />

            {/* Current: invisible base */}
            <Bar dataKey="currentBase" stackId="current" fill="transparent" radius={0} maxBarSize={32} />
            {/* Current: visible portion */}
            <Bar dataKey="currentValue" stackId="current" radius={[3, 3, 0, 0]} maxBarSize={32} name="Current">
              {chartData.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={BAR_COLORS[entry.id]?.current ?? "#6b7280"}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>

            {/* Improved: invisible base */}
            <Bar dataKey="improvedBase" stackId="improved" fill="transparent" radius={0} maxBarSize={32} />
            {/* Improved: visible portion */}
            <Bar dataKey="improvedValue" stackId="improved" radius={[3, 3, 0, 0]} maxBarSize={32} name="Target">
              {chartData.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={BAR_COLORS[entry.id]?.improved ?? "#d1d5db"}
                  fillOpacity={0.5}
                  stroke={BAR_COLORS[entry.id]?.current ?? "#6b7280"}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs text-text-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-blue-600 inline-block" /> Current
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm border-2 border-blue-600 border-dashed bg-blue-100 inline-block" /> 2-3yr Target
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-green-600 inline-block" /> Expansion
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-red-600 inline-block" /> Contraction
        </span>
      </div>

      {/* Improvement Levers (expandable) */}
      <div className="border-t border-surface-borderLight pt-4">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-mck-blue transition-colors"
        >
          Improvement Levers Detail
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showDetails && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-in">
            {waterfall
              .filter((w) => !isTotalBar(w.id) && w.improved_pct !== w.current_pct)
              .map((item) => {
                const delta = item.improved_pct - item.current_pct;
                const isPositive = delta > 0;
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg p-4 border ${
                      isPositive
                        ? "bg-green-50/50 border-green-200/50"
                        : "bg-red-50/50 border-red-200/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-text-primary">{item.label}</span>
                      <span
                        className={`text-sm font-bold ${
                          isPositive ? "text-green-700" : "text-red-700"
                        }`}
                      >
                        {item.current_pct > 0 ? "+" : ""}
                        {item.current_pct}% → {item.improved_pct > 0 ? "+" : ""}
                        {item.improved_pct}%
                      </span>
                    </div>
                    <p className="text-xs text-text-tertiary leading-relaxed">{item.explanation}</p>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
